import React from 'react';
import { Divider, Empty, Input, Spin, message, Select, Button, Space, Card } from 'antd';
import { YingshiAccount } from './types';
import { SwapOutlined } from '@ant-design/icons';

interface YingshiAccountSectionProps {
  yingshiAccount: YingshiAccount | null;
  userUid: string;
  yingshiLoading: boolean;
  setUserUid: (uid: string) => void;
  fetchYingshiAccount: (uid: string) => void;
  availableAccounts: YingshiAccount[];
  onSwitchAccount: (uid: string) => void;
}

const YingshiAccountSection: React.FC<YingshiAccountSectionProps> = ({
  yingshiAccount,
  userUid,
  yingshiLoading,
  setUserUid,
  fetchYingshiAccount,
  availableAccounts,
  onSwitchAccount,
}) => {
  // 在组件挂载时，如果有 userUid 但没有账户信息，自动搜索
  React.useEffect(() => {
    if (userUid && !yingshiAccount && !yingshiLoading) {
      console.log('账户组件自动搜索用户:', userUid);
      fetchYingshiAccount(userUid);
    }
  }, [userUid, yingshiAccount, yingshiLoading, fetchYingshiAccount]);

  // 已有的自动选择账户逻辑
  React.useEffect(() => {
    if (availableAccounts.length > 0 && !yingshiAccount && !userUid) {
      const firstAccount = availableAccounts[0];
      if (firstAccount && firstAccount.uid) {
        console.log('YingshiAccount 自动选择第一个账户:', firstAccount.uid);
        onSwitchAccount(firstAccount.uid);
      }
    }
  }, [availableAccounts, yingshiAccount, userUid, onSwitchAccount]);

  // Function to handle adding a new account
  const handleAddAccount = () => {
    // Clear the current UID to show the input field
    setUserUid("");
    // Clear the current account
    onSwitchAccount("");
  };

  // Helper function to safely truncate strings
  const safeSubstring = (str: string | undefined, start: number, end?: number) => {
    if (!str) return '';
    return str.substring(start, end);
  };

  // Helper function to format account display text
  const formatAccountLabel = (account: YingshiAccount) => {
    if (!account || !account.uid) return '未知账户';
    
    if (account.remark) {
      return account.remark;
    }
    
    return account.uid;
  };

  return (
    <div className="yingshi-section">
      <Divider orientation="left" style={{ margin: '8px 0' }}>
        <Space>
          萤石云账户
          {availableAccounts.length > 0 && (
            <Select 
              value={yingshiAccount?.uid || undefined}
              style={{ width: 350, marginLeft: 8 }}
              placeholder="选择账户"
              onChange={onSwitchAccount}
              dropdownMatchSelectWidth={true}
              optionLabelProp="label"
              popupMatchSelectWidth={false}
            >
              {availableAccounts.map(account => (
                <Select.Option 
                  key={account.uid || Math.random().toString()} 
                  value={account.uid || ''}
                  label={formatAccountLabel(account)}
                >
                  <div style={{ padding: '4px 8px' }}>
                    <div style={{ fontWeight: 500 }}>
                      {account.remark || '无备注'}
                    </div>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      {account.uid || '未知ID'}
                    </div>
                  </div>
                </Select.Option>
              ))}
            </Select>
          )}
        </Space>
      </Divider>
      <Spin spinning={yingshiLoading}>
        {yingshiAccount ? (
          <Card size="small" className="yingshi-account-info" style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ maxWidth: '80%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                <p style={{ margin: '0 0 4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  <strong>账户ID:</strong> {yingshiAccount.uid || '未知ID'}
                </p>
                <p style={{ margin: '0 0 4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  <strong>AppKey:</strong> {yingshiAccount.appKey || '未知'}
                </p>
                {yingshiAccount.remark && (
                  <p style={{ margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <strong>备注:</strong> {yingshiAccount.remark}
                  </p>
                )}
              </div>
            </div>
          </Card>
        ) : (
          <div className="yingshi-account-empty">
            <div style={{ marginBottom: 16, textAlign: 'center' }}>
              <Input.Search
                placeholder="输入账户UID"
                enterButton="查询"
                value={userUid}
                onChange={(e) => setUserUid(e.target.value)}
                onSearch={(value) => {
                  if (value) {
                    fetchYingshiAccount(value);
                  } else {
                    message.warning("请输入账户UID");
                  }
                }}
                style={{ width: 300 }}
              />
            </div>
            {availableAccounts.length > 0 && (
              <div style={{ textAlign: 'center' }}>
                <p>或选择已有账户:</p>
                <Space wrap>
                  {availableAccounts.map(account => (
                    <Button 
                      key={account.uid || Math.random().toString()} 
                      onClick={() => onSwitchAccount(account.uid || '')}
                      style={{ marginBottom: 8 }}
                    >
                      {account.remark || (account.uid ? safeSubstring(account.uid, 0, 8) + '...' : '未知账户')}
                    </Button>
                  ))}
                </Space>
              </div>
            )}
          </div>
        )}
      </Spin>
    </div>
  );
};

export default YingshiAccountSection; 